/* ==========================================================================
   goat-lp-effects.js — shared motion engine for the goat-lp-* homepage sections.
   Loaded via snippets/goat-lp-effects.liquid (inline gate + this file, defer).
   Dependency-free: one IntersectionObserver per effect, rAF for the count-up,
   no scroll listeners.

   Attribute API
     html.goat-lp-fx            set by the snippet gate (JS runs, IO available,
                                no prefers-reduced-motion) — nothing hides without it
     [data-lp-fx="<tokens>"]    section root; tokens: reveal, countup (others are CSS-only)
     [data-lp-reveal]           ""|"fade"|"scale"; gets .is-revealed when in view, then
                                the attribute is removed and .lp-revealed is set.
                                Anything at least partly in the first viewport at init
                                (top < innerHeight, incl. already scrolled past) reveals
                                immediately with its stagger; only below-the-fold
                                elements are observed
     [data-lp-stagger="80"]     container; step in ms for --lp-delay (index capped at 7)
     [data-lp-reveal-group]     container (e.g. X-scroll row); when it enters the viewport
                                all its [data-lp-reveal] reveal together (not observed singly)
     [data-lp-count]            counts its numeric text up once (prefix/suffix kept)

   window.GoatLP = { init(root) } — re-run by shopify:section:load in the editor.
   ========================================================================== */
(function () {
  'use strict';

  if (window.GoatLP) return;

  var REVEAL_SEL = '[data-lp-fx~="reveal"] [data-lp-reveal]';
  var COUNT_SEL = '[data-lp-fx~="countup"] [data-lp-count]';
  var STAGGER_SEL = '[data-lp-stagger]';
  var GROUP_SEL = '[data-lp-reveal-group]';
  var GROUP_SCOPED_SEL = '[data-lp-fx~="reveal"] [data-lp-reveal-group]';
  var STAGGER_DEFAULT = 80;
  var STAGGER_CAP = 7;
  var REVEAL_MS = 700;
  var REVEAL_GRACE_MS = 150;
  var COUNT_MS = 1400;
  var COUNT_RATIO = 0.49; // threshold 0.5, with float tolerance at the crossing

  /* ---------- Count parsing / formatting (DOM-free, unit-testable) ---------- */

  // "15,000" -> { prefix: '', value: 15000, sep: ',', dec: '', decimals: 0, suffix: '' }
  // "4,9"    -> { ..., value: 4.9, sep: '', dec: ',', decimals: 1 }
  // Returns null when the text holds no digit.
  function parseCount(text) {
    if (typeof text !== 'string') return null;
    var m = /^([^\d]*)(\d[\d.,\s]*)(.*)$/.exec(text);
    if (!m) return null;

    var prefix = m[1];
    var num = m[2];
    var suffix = m[3];

    // Trailing whitespace / separators belong to the suffix ("8 +", "60.").
    var tail = /[.,\s]+$/.exec(num);
    if (tail) {
      suffix = tail[0] + suffix;
      num = num.slice(0, num.length - tail[0].length);
    }

    var dec = '';
    var decimals = 0;
    var intPart = num;
    var fracPart = '';
    // One separator followed by 1–2 digits at the end = decimal separator.
    var dm = /^(.*\d)([.,])(\d{1,2})$/.exec(num);
    if (dm) {
      intPart = dm[1];
      dec = dm[2];
      fracPart = dm[3];
      decimals = fracPart.length;
    }

    var sepMatch = /[^\d]/.exec(intPart);
    var sep = sepMatch ? sepMatch[0] : '';
    var digits = intPart.replace(/[^\d]/g, '');
    if (!digits) return null;

    var value = parseFloat(digits + (fracPart ? '.' + fracPart : ''));
    if (!isFinite(value)) return null;

    return {
      prefix: prefix,
      value: value,
      sep: sep,
      dec: dec,
      decimals: decimals,
      suffix: suffix
    };
  }

  function formatCount(parsed, value) {
    var fixed = value.toFixed(parsed.decimals);
    var parts = fixed.split('.');
    var intStr = parts[0];
    if (parsed.sep) {
      intStr = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, parsed.sep);
    }
    var out = intStr;
    if (parsed.decimals > 0) out += (parsed.dec || '.') + parts[1];
    return parsed.prefix + out + parsed.suffix;
  }

  function easeOutCubic(t) {
    var p = 1 - t;
    return 1 - p * p * p;
  }

  var enabled = document.documentElement.classList.contains('goat-lp-fx');

  window.GoatLP = {
    init: function (root) {
      if (enabled) init(root || document);
    },
    _parseCount: parseCount,
    _formatCount: formatCount
  };

  // Reduced motion / no IntersectionObserver / asset too late: CSS hides nothing.
  if (!enabled) return;

  /* ---------- Helpers ---------- */

  function qsa(root, sel) {
    var list = Array.prototype.slice.call(root.querySelectorAll(sel));
    if (root.nodeType === 1 && root.matches(sel)) list.unshift(root);
    return list;
  }

  /* ---------- Reveal ---------- */

  var revealTracked = new WeakSet();

  function finishReveal(el) {
    if (el.__goatLpDone) return;
    el.__goatLpDone = true;
    el.removeAttribute('data-lp-reveal');
    el.style.removeProperty('--lp-delay');
    el.classList.add('lp-revealed');
  }

  function reveal(el) {
    if (el.classList.contains('is-revealed')) return;
    var delay = parseFloat(el.style.getPropertyValue('--lp-delay')) || 0;
    var timer = window.setTimeout(function () {
      finishReveal(el);
    }, REVEAL_MS + delay + REVEAL_GRACE_MS);
    el.addEventListener('transitionend', function onEnd(e) {
      if (e.target !== el || e.propertyName !== 'opacity') return;
      el.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
      finishReveal(el);
    });
    el.classList.add('is-revealed');
  }

  var revealIO = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        // Already scrolled past (reload mid-page, anchor jump): show right away.
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
          revealIO.unobserve(el);
          reveal(el);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  // At init: partly inside the first viewport, or already scrolled past.
  function inFirstViewport(el) {
    return el.getBoundingClientRect().top < window.innerHeight;
  }

  function applyStagger(root) {
    qsa(root, STAGGER_SEL).forEach(function (container) {
      var step = parseFloat(container.getAttribute('data-lp-stagger'));
      if (!isFinite(step) || step < 0) step = STAGGER_DEFAULT;
      var index = 0;
      Array.prototype.forEach.call(container.querySelectorAll('[data-lp-reveal]'), function (el) {
        var parent = el.parentElement;
        if (!parent || parent.closest(STAGGER_SEL) !== container) return;
        if (revealTracked.has(el)) {
          index += 1;
          return;
        }
        el.style.setProperty('--lp-delay', Math.min(index, STAGGER_CAP) * step + 'ms');
        index += 1;
      });
    });
  }

  // Groups (e.g. horizontal scroll rows): when the container enters the viewport,
  // every [data-lp-reveal] inside reveals at once with its stagger delay, so items
  // that only peek in at the row edges (or sit off-screen in X) are not left hidden.
  var groupTracked = new WeakSet();
  var groupFired = new WeakSet();

  function revealGroup(group) {
    groupFired.add(group);
    Array.prototype.forEach.call(group.querySelectorAll('[data-lp-reveal]'), function (el) {
      if (el.closest(GROUP_SEL) !== group) return;
      revealTracked.add(el);
      revealIO.unobserve(el);
      reveal(el);
    });
  }

  var groupIO = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var group = entry.target;
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
          groupIO.unobserve(group);
          revealGroup(group);
        }
      });
    },
    { threshold: 0, rootMargin: '0px 0px -8% 0px' }
  );

  function initReveal(root) {
    applyStagger(root);
    qsa(root, GROUP_SCOPED_SEL).forEach(function (group) {
      if (groupTracked.has(group)) return;
      groupTracked.add(group);
      if (inFirstViewport(group)) {
        groupIO.unobserve(group);
        revealGroup(group);
      } else {
        groupIO.observe(group);
      }
    });
    qsa(root, REVEAL_SEL).forEach(function (el) {
      if (revealTracked.has(el)) return;
      var group = el.closest(GROUP_SEL);
      if (group && groupTracked.has(group)) {
        // Owned by its group; reveal now if the group already fired.
        revealTracked.add(el);
        if (groupFired.has(group)) reveal(el);
        return;
      }
      revealTracked.add(el);
      if (inFirstViewport(el)) {
        reveal(el);
      } else {
        revealIO.observe(el);
      }
    });
  }

  /* ---------- Count-up ---------- */

  var countTracked = new WeakSet();
  var countData = new WeakMap();

  function runCount(el) {
    var data = countData.get(el);
    if (!data) return;
    var start = null;
    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / COUNT_MS, 1);
      if (t >= 1) {
        el.textContent = data.original;
        return;
      }
      el.textContent = formatCount(data.parsed, data.parsed.value * easeOutCubic(t));
      window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);
  }

  var countIO = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        var data = countData.get(el);
        if (!data) {
          countIO.unobserve(el);
          return;
        }
        if (entry.isIntersecting && entry.intersectionRatio >= COUNT_RATIO) {
          countIO.unobserve(el);
          runCount(el);
        } else if (entry.isIntersecting) {
          // Entering from below: show zero until half of it is in view.
          // Entering from above (already scrolled past): keep the real value.
          if (entry.boundingClientRect.top >= 0 && !data.zeroed) {
            data.zeroed = true;
            el.textContent = formatCount(data.parsed, 0);
          }
        } else if (data.zeroed) {
          // Left the viewport before counting: never leave a fake zero behind.
          data.zeroed = false;
          el.textContent = data.original;
        }
      });
    },
    { threshold: [0, 0.5] }
  );

  function initCount(root) {
    qsa(root, COUNT_SEL).forEach(function (el) {
      if (countTracked.has(el)) return;
      countTracked.add(el);
      if (el.children.length) return; // only plain-text values; never destroy markup
      var original = el.textContent;
      var parsed = parseCount(original);
      if (!parsed) return;
      countData.set(el, { original: original, parsed: parsed, zeroed: false });
      countIO.observe(el);
    });
  }

  /* ---------- Boot ---------- */

  function init(root) {
    initReveal(root);
    initCount(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) init(e.target);
  });
})();
